import { useMemo, useContext, useEffect, useCallback } from 'react';
import { Layer, Marker, Source } from 'react-map-gl';
import { FormattedMessage, useIntl } from 'react-intl';
import { MapMouseEvent } from 'mapbox-gl';
import { type Point, featureCollection, point } from '@turf/helpers';
import logCustomRouteUpdated from '@alltrails/amplitude/events/logCustomRouteUpdated';
import { useGroupHover } from '@alltrails/core';
import { COLOR_NEUTRAL_WHITE, COLOR_TEXT_PRIMARY, FONT_SIZE_100 } from '@alltrails/denali/tokens';
import { useExperiments } from '@alltrails/experiments';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider';
import { useSelector, useDispatch } from '../../redux';
import { updateCursor } from '../../redux/reducer';
import { poiPinHeightMd, poiPinIconWidthMd, poiPinVerticalOffset } from '../../utils/svgDefinitions/svgMarkerComponents';
import {
  clearWaypointEditingState,
  setWaypointEditingState,
  updateWaypoint,
  setActiveWaypointPopup,
  clearActiveWaypointPopup
} from '../../redux/customRoute';
import useSvgImages from '../../utils/useSvgImages';
import { customRouteWaypointsId, defaultPOIPinLayerLayoutProps, defaultSymbolLayerLayoutProps } from '../../utils/layers';
import createWaypointPinSvg from '../../utils/svgDefinitions/createWaypointPinSvg';
import { WAYPOINT_ZOOM } from '../../utils/customRoutes/constants';
import useMap from '../../hooks/useMap';
import WaypointRoutingOptions from './Waypoints/WaypointRoutingOptions';
import styles from './Waypoints.module.scss';

export const SOURCE_ID = 'custom-route-waypoints-source';

type WaypointsProps = {
  canEdit?: boolean;
};

const Waypoints = ({ canEdit = false }: WaypointsProps) => {
  const dispatch = useDispatch();
  const map = useMap();
  const intl = useIntl();
  const hasWaypointsExperiment = useExperiments()?.['web-discover-byot-waypoints-rm']?.value === 'on';
  const { activeWaypointId, selectedWaypointId, activeWaypointPopupId, waypoints, isEditingWaypoint, isEditing, draftWaypoint } = useSelector(
    state => ({
      activeWaypointId: state.customRoute.activeWaypointId,
      selectedWaypointId: state.customRoute.selectedWaypointId,
      activeWaypointPopupId: state.customRoute.activeWaypointPopupId,
      waypoints: state.customRoute.route.waypoints,
      isEditingWaypoint: state.customRoute.isEditingWaypoint,
      isEditing: state.customRoute.isEditing,
      draftWaypoint: state.customRoute.draftWaypoint
    })
  );

  const selectedWaypoint = useMemo(() => {
    if (selectedWaypointId === undefined) return undefined;
    const waypoint = waypoints.find(({ id }) => id === selectedWaypointId);
    if (!waypoint) return undefined;

    if (draftWaypoint && draftWaypoint.id === selectedWaypointId) {
      return {
        ...waypoint,
        ...draftWaypoint
      };
    }
    return waypoint;
  }, [selectedWaypointId, waypoints, draftWaypoint]);

  // Memoize these in the component instead of defining them as constants since they need access to the 'window' object
  const activeSvg = useMemo(() => createWaypointPinSvg('md'), []);
  const inactiveSvg = useMemo(() => createWaypointPinSvg('xs'), []);
  const svgDefinitions = useMemo(() => [activeSvg, inactiveSvg], [activeSvg, inactiveSvg]);
  const { imagesAreLoaded } = useSvgImages(svgDefinitions);
  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);

  useEffect(() => {
    if (!map || selectedWaypoint === undefined || selectedWaypoint === null) return;

    const { latitude, longitude } = selectedWaypoint;
    const pointCoords: [number, number] = [longitude, latitude];

    map.easeTo({ center: pointCoords, zoom: WAYPOINT_ZOOM });
  }, [map, selectedWaypoint]);

  const onDragEnd = useCallback(
    (event: { lngLat: { lng: number; lat: number } }, waypointId: number) => {
      const waypoint = waypoints.find(wp => wp.id === waypointId);
      if (waypoint) {
        dispatch(
          updateWaypoint({
            id: waypointId,
            name: waypoint.name,
            description: waypoint.description || '',
            latitude: Number(event.lngLat.lat.toFixed(6)),
            longitude: Number(event.lngLat.lng.toFixed(6)),
            showTitle: waypoint.showTitle || false
          })
        );
      }
    },
    [dispatch, waypoints]
  );

  const onHover = useCallback(
    event => {
      if (isEditingWaypoint) return;

      const id = event.id;
      if (id !== undefined) {
        dispatch(setWaypointEditingState({ activeId: id }));
        // Behind feature flag: show pointer when in editing mode, even if canEdit prop is false
        const canInteract = hasWaypointsExperiment ? isEditing : canEdit;
        if (canInteract) {
          dispatch(updateCursor('pointer'));
        }
      }
    },
    [canEdit, dispatch, isEditingWaypoint, isEditing, hasWaypointsExperiment]
  );

  const onHoverEnd = useCallback(() => {
    if (isEditingWaypoint) return;

    if (activeWaypointId !== undefined) {
      dispatch(clearWaypointEditingState());
      dispatch(updateCursor('unset'));
    }
  }, [dispatch, activeWaypointId, isEditingWaypoint]);

  const { onMouseEnter, onMouseLeave } = useGroupHover(onHover, onHoverEnd, true /* isMouseMove */, 50 /* debounceTime */);

  const waypointsData = useMemo(
    () =>
      featureCollection<Point>(
        waypoints
          .filter(waypoint => waypoint.id !== selectedWaypointId)
          .map(waypoint => {
            const { id, name, showTitle, latitude, longitude } = waypoint;
            const title = showTitle ? (name === '' ? intl.formatMessage({ defaultMessage: 'Waypoint {name}' }, { name: id + 1 }) : name) : undefined;
            const activeOrSelected = id === activeWaypointId || id === activeWaypointPopupId;

            return point([longitude, latitude], {
              title,
              activeOrSelected,
              id
            });
          })
      ),
    [waypoints, activeWaypointId, activeWaypointPopupId, selectedWaypointId, intl]
  );

  const onMouseMove = useCallback(
    (event: MapMouseEvent) => {
      if (isEditingWaypoint) return;

      if (onMouseEnter && event.features) {
        const spot = event.features[0].properties;
        onMouseEnter(spot);
      }
    },
    [onMouseEnter, isEditingWaypoint]
  );

  const onClick = useCallback(
    (event: MapMouseEvent) => {
      if (event.features) {
        event.preventDefault(); // Allows us to detect when a user clicks on the map but NOT on this layer
        const canInteract = hasWaypointsExperiment ? isEditing : canEdit;

        if (isEditingWaypoint || !canInteract) return;

        const feature = event.features?.[0];
        const id = feature?.properties?.id;

        if (id !== undefined) {
          if (hasWaypointsExperiment) {
            dispatch(setActiveWaypointPopup(id));
          } else {
            dispatch(setWaypointEditingState({ selectedId: id }));
            logCustomRouteUpdated({ entry_point_location: 'waypoint clicked' });
          }
        }
      }
    },
    [canEdit, dispatch, isEditingWaypoint, isEditing, hasWaypointsExperiment]
  );

  useEffect(() => {
    registerEventHandler(customRouteWaypointsId, 'mousemove', onMouseMove);
    onMouseLeave && registerEventHandler(customRouteWaypointsId, 'mouseleave', onMouseLeave);
    registerEventHandler(customRouteWaypointsId, 'click', onClick);
    return () => {
      unregisterEventHandler(customRouteWaypointsId, 'click', onClick);
      unregisterEventHandler(customRouteWaypointsId, 'mousemove', onMouseMove);
      onMouseLeave && unregisterEventHandler(customRouteWaypointsId, 'mouseleave', onMouseLeave);
    };
  }, [onMouseLeave, onMouseEnter, onMouseMove, onClick, registerEventHandler, unregisterEventHandler]);

  useEffect(() => {
    if (!hasWaypointsExperiment) return;

    const removeActiveStates = (event: MapMouseEvent) => {
      // We prevent default when a user clicks on this layer
      if (!event.defaultPrevented) {
        dispatch(clearActiveWaypointPopup());
      }
    };

    map?.on('click', removeActiveStates);
    map?.on('movestart', removeActiveStates);
    return () => {
      map?.off('click', removeActiveStates);
      map?.off('movestart', removeActiveStates);
    };
  }, [dispatch, map, hasWaypointsExperiment]);

  if (!imagesAreLoaded) {
    return null;
  }

  return (
    <>
      <Source id={SOURCE_ID} type="geojson" data={waypointsData}>
        {/* Icon Layer - always renders all waypoint pins */}
        <Layer
          id={customRouteWaypointsId}
          source={SOURCE_ID}
          type="symbol"
          layout={{
            ...defaultSymbolLayerLayoutProps,
            ...defaultPOIPinLayerLayoutProps,
            'icon-allow-overlap': true,
            'icon-image': ['case', ['get', 'activeOrSelected'], activeSvg.name, inactiveSvg.name],
            // Higher value = render on top (when icon-allow-overlap is true)
            // When not active/selected, use negative ID so lower IDs render on top
            'symbol-sort-key': ['case', ['get', 'activeOrSelected'], 1, ['-', 0, ['/', ['get', 'id'], 1000]]]
          }}
        />
        {/* Text Label Layer - uses collision detection to show only one label when clumped */}
        <Layer
          id={`${customRouteWaypointsId}-labels`}
          source={SOURCE_ID}
          type="symbol"
          layout={{
            ...defaultSymbolLayerLayoutProps,
            ...defaultPOIPinLayerLayoutProps,
            'text-field': ['get', 'title'],
            'text-offset': [0, poiPinVerticalOffset / FONT_SIZE_100],
            'text-size': FONT_SIZE_100,
            // Lower value = priority when text-allow-overlap is false
            // Active/selected gets very low value (-1000) to always win collision detection
            // Inactive waypoints use ID/1000 so lower IDs get priority
            'symbol-sort-key': ['case', ['get', 'activeOrSelected'], -1000, ['/', ['get', 'id'], 1000]],
            'text-allow-overlap': false,
            'text-ignore-placement': false,
            'text-optional': true
          }}
          paint={{
            'text-halo-color': COLOR_NEUTRAL_WHITE,
            'text-halo-width': 1.5,
            'text-color': COLOR_TEXT_PRIMARY
          }}
        />
      </Source>
      {selectedWaypoint && (
        <Marker
          longitude={selectedWaypoint.longitude}
          latitude={selectedWaypoint.latitude}
          draggable={isEditingWaypoint}
          onDragEnd={event => onDragEnd(event, selectedWaypoint.id)}
          anchor="bottom"
        >
          <div
            className={styles.container}
            style={{
              width: poiPinIconWidthMd,
              height: poiPinHeightMd,
              backgroundImage: `url(data:image/svg+xml;base64,${window.btoa(activeSvg.svgString)})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              cursor: isEditingWaypoint ? 'grab' : 'pointer'
            }}
          >
            {selectedWaypoint.showTitle && (
              <div className={styles.title}>
                {selectedWaypoint.name === '' ? (
                  <FormattedMessage defaultMessage="Waypoint {name}" values={{ name: selectedWaypoint.id + 1 }} />
                ) : (
                  selectedWaypoint.name
                )}
              </div>
            )}
          </div>
        </Marker>
      )}
      {hasWaypointsExperiment && <WaypointRoutingOptions />}
    </>
  );
};

export default Waypoints;
